'use client';
import IdentityDelegationsForm from '../components/IdentityDelegationsForm';
import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import IdentityRewardsTabContent from '../components/IdentityRewardsTabContent';
import IdentityInfoTabContent from '../components/IdentityInfoTabContent';
import { isOfficialIdentity } from '../utils/identityUtils';
import OfficialBadge from '../components/OfficialBadge';
import { useGetIdentityDataFromContract } from '../hooks/useGetIdentityDataFromContract';
import { useAccountChainIdOrDefault } from '../hooks/useAccountChainIdOrDefault';
import { useEvmWallet } from '../hooks/useEvmWallet';
import type { Delegation } from '../types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import KaiTokenAmount from '../components/KaiTokenAmount';
import {
  Loader2,
  Copy,
  Check,
  AlertCircle,
  Info,
  Gift,
  Users,
  ArrowLeft,
  Coins,
  Trophy,
  User,
  Shield,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useRewardsLeaderboard } from '../hooks/useRewardsLeaderboard';

// Stat Card Component
function StatCard({
  icon: Icon,
  label,
  value,
  subValue,
  className,
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  subValue?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'p-4 rounded-xl border border-divider bg-bg-secondary/20',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-bg-tertiary/50 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-text-tertiary" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
            {label}
          </p>
          <div className="text-lg font-semibold text-text-default">{value}</div>
          {subValue && (
            <p className="text-xs text-text-tertiary mt-0.5">{subValue}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function IdentityPage() {
  const [copied, setCopied] = useState(false);
  let { identity } = useParams();
  if (!identity) {
    identity = '';
  }

  const chainId = useAccountChainIdOrDefault();
  const { address } = useEvmWallet();
  const {
    data: identityData,
    isLoading,
    refetch,
    error,
  } = useGetIdentityDataFromContract(chainId ?? 0, identity, !!chainId);

  const isOwner =
    identityData?.nft.owner.toLowerCase() === address?.toLowerCase();

  // Rewards points from leaderboard
  const { data: leaderboard = [] } = useRewardsLeaderboard();
  const rewardsPoints = useMemo(() => {
    const match = leaderboard.find(
      (entry) => entry.identity === `@@${identity}`,
    );
    return match?.points ?? 0;
  }, [leaderboard, identity]);

  const handleCopyReferral = () => {
    navigator.clipboard.writeText(
      `${window.location.host}/?referrer=${identity}`,
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const truncateAddress = (addr: string) => {
    if (!addr) return '';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  return (
    <main className="grow pb-20 px-6 font-inter">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-text-tertiary hover:text-text-default transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-brand" />
            <p className="text-sm text-text-tertiary">Loading identity...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="p-8 rounded-2xl border border-destructive/20 bg-destructive/5 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-destructive/10 mx-auto flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-destructive" />
            </div>
            <div>
              <p className="text-text-default font-medium">
                Error loading identity
              </p>
              <p className="text-sm text-text-secondary mt-1">
                An error occurred when fetching identity data.
              </p>
            </div>
          </div>
        )}

        {/* Not Found State */}
        {!isLoading && !error && identityData === null && (
          <div className="p-8 rounded-2xl border border-divider bg-bg-secondary/20 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-bg-tertiary mx-auto flex items-center justify-center">
              <Info className="w-5 h-5 text-text-tertiary" />
            </div>
            <div>
              <p className="text-text-default font-medium">
                Identity not registered
              </p>
              <p className="text-sm text-text-secondary mt-1">
                If you've just registered this identity, please wait 1-2
                minutes.
              </p>
            </div>
          </div>
        )}

        {/* Identity Content */}
        {!isLoading && !error && identityData && (
          <>
            {/* Hero Section */}
            <div className="rounded-2xl border border-divider bg-bg-secondary/20 overflow-hidden">
              {/* Header */}
              <div className="px-6 py-5 border-b border-divider bg-bg-tertiary/30">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center">
                      <User className="w-6 h-6 text-brand" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h1 className="font-clash text-2xl font-medium text-text-default break-all">
                          @@{identity}
                        </h1>
                        {isOfficialIdentity(identity) && <OfficialBadge />}
                      </div>
                      {isOwner && (
                        <span className="inline-flex items-center gap-1 text-xs text-success mt-1">
                          <Shield className="w-3 h-3" />
                          You own this identity
                        </span>
                      )}
                    </div>
                  </div>
                  {isOwner && (
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={handleCopyReferral}
                      className="gap-2 border-divider shrink-0"
                    >
                      {copied ? (
                        <>
                          <Check className="w-3.5 h-3.5 text-success" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5" />
                          Copy referral link
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {/* Stats Grid */}
              <div className="p-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <StatCard
                    icon={Coins}
                    label="Staked KAI"
                    value={
                      <KaiTokenAmount
                        amount={BigInt(identityData.stakedTokens)}
                      />
                    }
                  />
                  <StatCard
                    icon={Users}
                    label="Delegated KAI"
                    value={
                      <KaiTokenAmount
                        amount={BigInt(identityData.delegatedTokens ?? '0')}
                      />
                    }
                    subValue={`${
                      identityData.delegations?.length ?? 0
                    } delegation${
                      identityData.delegations?.length !== 1 ? 's' : ''
                    }`}
                  />
                  <StatCard
                    icon={Trophy}
                    label="Rewards Points"
                    value={new Intl.NumberFormat().format(rewardsPoints)}
                  />
                  <StatCard
                    icon={User}
                    label="Owner"
                    value={
                      <span className="text-sm font-mono">
                        {truncateAddress(identityData.nft.owner)}
                      </span>
                    }
                    subValue={`NFT #${identityData.nft.tokenId}`}
                  />
                </div>
              </div>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="info" className="w-full">
              <TabsList className="w-full sm:w-auto grid grid-cols-3 sm:inline-flex">
                <TabsTrigger value="info" className="gap-2">
                  <Info className="w-4 h-4 hidden sm:block" />
                  Info
                </TabsTrigger>
                <TabsTrigger value="rewards" className="gap-2">
                  <Gift className="w-4 h-4 hidden sm:block" />
                  Rewards
                </TabsTrigger>
                <TabsTrigger value="delegations" className="gap-2">
                  <Users className="w-4 h-4 hidden sm:block" />
                  Delegations
                </TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="mt-6">
                <div className="rounded-2xl border border-divider bg-bg-secondary/20 p-5">
                  <IdentityInfoTabContent
                    identityData={identityData}
                    refetch={refetch}
                  />
                </div>
              </TabsContent>

              <TabsContent value="rewards" className="mt-6">
                <div className="rounded-2xl border border-divider bg-bg-secondary/20 p-5">
                  <IdentityRewardsTabContent identityData={identityData} />
                </div>
              </TabsContent>

              <TabsContent value="delegations" className="mt-6">
                <div className="rounded-2xl border border-divider bg-bg-secondary/20 p-5">
                  <IdentityDelegationsForm
                    identity={identity}
                    stakedAmount={BigInt(identityData.stakedTokens)}
                    delegations={identityData.delegations.map(
                      (del: Delegation) => {
                        return {
                          delegatee: {
                            name: del.delegatee.substring(
                              0,
                              del.delegatee.lastIndexOf('.'),
                            ),
                            namespace: del.delegatee.substring(
                              del.delegatee.lastIndexOf('.'),
                            ),
                          },
                          amount: BigInt(del.amount),
                        };
                      },
                    )}
                    delegatedToThis={BigInt(
                      identityData.delegatedTokens ?? '0',
                    )}
                    isOwner={isOwner}
                    refetch={refetch}
                  />
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </main>
  );
}
